import React, {useMemo, useState} from 'react';

import * as Analytics from 'analytics/Analytics';
import {ActionOrigin, AnalyticsMetadataForContext, AnalyticsModalType} from 'constants/AnalyticsConstants';
import {MinimalFreezeThawInfo} from 'constants/SampleStatusConstants';
import {ContainerPropertiesUpdate} from 'samples/ContainerDAO';
import {bulkUpdateContainerPropertiesWithDataMods} from 'samples/StorableActions';
import BaseBulkEditSamplesModal from 'samples/toolbar/BaseBulkEditSamplesModal';
import CurrentAndNewValueRow from 'samples/toolbar/CurrentAndNewValueRow';
import NumericButtonInput from 'ui-library/inputs/NumericButtonInput';

export interface BulkUpdateFreezeThawCountModalProps {
  onRequestClose: () => void;
  onRequestSubmit: (selectedIds: Array<string>) => void;
  analyticsMetadata: AnalyticsMetadataForContext;
  items: Array<{id: string; freezeThawInfo?: MinimalFreezeThawInfo}>;
}

const MULTIPLE_VALUES_SENTINEL = 'MULTIPLE_VALUES_SENTINEL';
function getCurrentFreezeThawCount(
  items: Array<{id: string; freezeThawInfo?: MinimalFreezeThawInfo}>
): number | null | typeof MULTIPLE_VALUES_SENTINEL {
  const dedupedCurrentValues = new Set(items.map((item) => item.freezeThawInfo?.freezeThawCount ?? null));
  if (dedupedCurrentValues.size === 1) {
    const [date] = dedupedCurrentValues;
    return date;
  } else {
    return MULTIPLE_VALUES_SENTINEL;
  }
}

export default function BulkUpdateFreezeThawCountModal(
  props: BulkUpdateFreezeThawCountModalProps
): JSX.Element {
  const {currentCount, originalFreezeThawCount, itemIds} = useMemo(() => {
    const currentCount = getCurrentFreezeThawCount(props.items);
    return {
      currentCount,
      originalFreezeThawCount: currentCount === MULTIPLE_VALUES_SENTINEL ? null : currentCount,
      itemIds: props.items.map(({id}) => id),
    };
  }, [props.items]);

  const [freezeThawCount, setFreezeThawCount] = useState<number | null>(originalFreezeThawCount);
  const [shouldClearFreezeThaw, setShouldClearFreezeThaw] = useState(false);

  const analyticsMetadataDict = {
    ...props.analyticsMetadata,
    modalType: AnalyticsModalType.BULK_UPDATE_FREEZE_THAW_COUNT,
  };

  const handleRequestSubmit = (selectedIds: Array<string>, freezeThawCount: number | null): void => {
    const containerUpdates: Array<ContainerPropertiesUpdate> = selectedIds.map((id) => ({
      id,
      freezeThawCount,
    }));
    bulkUpdateContainerPropertiesWithDataMods(containerUpdates, analyticsMetadataDict);
    props.onRequestClose();
  };

  const onChangeClearFreezeThaw = (clearFreezeThaw: boolean): void => {
    setShouldClearFreezeThaw(clearFreezeThaw);
    setFreezeThawCount(clearFreezeThaw ? null : originalFreezeThawCount);
  };

  const modalContent = (
    <CurrentAndNewValueRow
      label="value"
      currentValue={
        currentCount === MULTIPLE_VALUES_SENTINEL
          ? 'Multiple values'
          : (currentCount ?? 'Not specified').toString()
      }
      newValueInput={
        <NumericButtonInput
          value={freezeThawCount}
          onChangeValue={({value}) => {
            setFreezeThawCount(value);
          }}
          minValue={0}
          disabled={shouldClearFreezeThaw}
        />
      }
    />
  );

  return (
    <BaseBulkEditSamplesModal
      modalTitle="Bulk update freeze/thaw count"
      onRequestClose={props.onRequestClose}
      onRequestSubmit={(selectedIds) => {
        handleRequestSubmit(selectedIds, freezeThawCount);
      }}
      itemIds={itemIds}
      analyticsMetadata={analyticsMetadataDict}
      modalContent={modalContent}
      clearFieldCheckboxProps={{
        shouldShowClearCheckbox: () => currentCount != null,
        shouldClear: shouldClearFreezeThaw,
        onChangeShouldClear: onChangeClearFreezeThaw,
      }}
      isSubmitDisabled={() => freezeThawCount == null && !shouldClearFreezeThaw}
      dataTestAttrs={{component: 'BulkUpdateFreezeThawCountModal', element: 'root'}}
      sampleStatusParams={{
        trackingParams: {
          actionAttempted: 'update_freeze_thaw_count',
          actionOrigin: props.analyticsMetadata.actionOrigin ?? ActionOrigin.INVENTORY_REGISTRY_SEARCH_UI,
          sampleIds: itemIds,
          eventGroupId: props.analyticsMetadata.eventGroupId ?? Analytics.makeAnalyticsEventId(),
        },
      }}
    />
  );
}
